
import React from 'react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle, AlertCircle } from "lucide-react";
import { ValidationResult } from '../types/automationTypes';

interface RuleValidationDisplayProps {
  validationResult: ValidationResult;
  isValidating?: boolean;
  className?: string;
}

export const RuleValidationDisplay: React.FC<RuleValidationDisplayProps> = ({
  validationResult,
  isValidating = false,
  className = "",
}) => {
  if (isValidating) {
    return (
      <Alert className={className}>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Validating Rule...</AlertTitle>
        <AlertDescription>
          Checking rule configuration and constraints.
        </AlertDescription>
      </Alert>
    );
  }

  if (validationResult.isValid && validationResult.warnings.length === 0) {
    return (
      <Alert className={`border-green-200 bg-green-50 ${className}`}>
        <CheckCircle className="h-4 w-4 text-green-600" />
        <AlertTitle className="text-green-800">Rule Valid</AlertTitle>
        <AlertDescription className="text-green-700">
          This automation rule is properly configured and ready to use.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {validationResult.errors.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Validation Errors</AlertTitle>
          <AlertDescription>
            <ul className="list-disc list-inside space-y-1 mt-2">
              {validationResult.errors.map((error, index) => (
                <li key={index} className="text-sm">{error}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {validationResult.warnings.length > 0 && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertTitle className="text-yellow-800">Warnings</AlertTitle>
          <AlertDescription className="text-yellow-700">
            <ul className="list-disc list-inside space-y-1 mt-2">
              {validationResult.warnings.map((warning, index) => (
                <li key={index} className="text-sm">{warning}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {validationResult.isValid && (
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-green-700 border-green-300">
            <CheckCircle className="h-3 w-3 mr-1" />
            Valid Configuration
          </Badge>
        </div>
      )}
    </div>
  );
};
